/* eslint-disable @typescript-eslint/no-restricted-imports */
import { Controller, type UseFormMethods } from 'react-hook-form';

import Checkbox from '~/common/components/Checkboxes/Checkbox/Checkbox';
import CheckboxGroup from '~/common/components/Checkboxes/CheckboxGroup';
import Flex from '~/common/components/Flex';
import FormControl from '~/common/components/Forms/FormControl';
import FormLabel from '~/common/components/Forms/FormLabel';

const disabledStyles = {
  opacity: 0.6,
};

type FormBuilderCheckboxProps = {
  register?: (...args: any[]) => any;
  name?: string;
  label?: string;
  span?: number;
  value?: any;
  handleChange?: (...args: any[]) => any;
  readOnly?: boolean;
  getValues?: (...args: any[]) => any;
  onChangeHandler?: (...args: any[]) => any;
  position?: string;
  isChecked?: (...args: any[]) => any;
  isRequired?: boolean;
  validate?: (...args: any[]) => any;
  disabled?: boolean;
  tabIndex?: number;
  control?: UseFormMethods['control'];
  useHooksForm?: boolean;
  checkboxStyle?: React.CSSProperties;
};

// SINGLE CHECKBOX

export const FormBuilderCheckbox = ({
  name,
  label,
  position,
  value,
  isChecked,
  disabled = false,
  readOnly = false,
  onChangeHandler,
  getValues,
  tabIndex,
  useHooksForm,
  control,
  checkboxStyle,
}: Readonly<FormBuilderCheckboxProps>) => {
  const checkboxContent = (
    <FormControl
      padding={'0rem 1rem'}
      style={{
        height: '100%',
        display: 'flex',
        alignItems: position ?? 'flex-end',
      }}
    >
      <Checkbox
        tabIndex={tabIndex}
        isChecked={isChecked ? isChecked() : getValues(value)}
        style={{
          ...(disabled ? disabledStyles : {}),
          ...checkboxStyle,
        }}
        isDisabled={disabled ?? false}
        isReadOnly={readOnly ?? false}
        onChange={() => {
          if (!readOnly && !disabled) onChangeHandler(getValues(name));
        }}
      >
        {label}
      </Checkbox>
    </FormControl>
  );
  if (useHooksForm) {
    return (
      <Controller
        name={name}
        control={control}
        render={({ value, onChange }) => {
          return (
            <FormControl
              padding={'0rem 1rem'}
              style={{
                height: '100%',
                display: 'flex',
                alignItems: position ?? 'flex-end',
              }}
            >
              <Checkbox
                tabIndex={tabIndex}
                style={{
                  ...(disabled ? disabledStyles : {}),
                  ...checkboxStyle,
                }}
                isDisabled={disabled ?? false}
                isReadOnly={readOnly ?? false}
                isChecked={value}
                onChange={({ target: { checked } }) => {
                  onChange(checked);
                }}
              >
                {label}
              </Checkbox>
            </FormControl>
          );
        }}
      />
    );
  }
  return checkboxContent;
};

type FormBuilderCheckboxGroupProps = {
  register?: (...args: any[]) => any;
  name?: string;
  label?: string;
  span?: number;
  isInvalid?: boolean;
  options?: {
    value?: string;
    label?: string;
    isHidden?: () => boolean;
  }[];
  isRequired?: boolean;
  errors?: any;
  validate?: (...args: any[]) => any;
  displayValue?: (...args: any[]) => any;
  isChecked?: (...args: any[]) => any;
  onChangeHandler?: (...args: any[]) => any;
  readOnly?: boolean;
  disabled?: boolean;
  tabIndex?: number;
  dataTestId?: string;
};

// MULTIPLE CHECKBOXES
export const FormBuilderCheckboxGroup = ({
  name,
  label,
  options,
  isInvalid,
  isRequired = false,
  isChecked,
  onChangeHandler,
  displayValue,
  readOnly,
  disabled,
  tabIndex,
  dataTestId,
}: FormBuilderCheckboxGroupProps) => (
  <FormControl isRequired={isRequired}>
    <CheckboxGroup tabIndex={tabIndex}>
      <FormLabel htmlFor={name}>{label}</FormLabel>
      <Flex>
        {options.map(
          (option) =>
            (option.isHidden ? !option.isHidden() : true) && (
              <Checkbox
                tabIndex={tabIndex}
                isInvalid={isInvalid ?? false}
                data-testid={`${dataTestId}-${option.value}`}
                key={option.value}
                style={{ marginRight: '1.5rem', opacity: disabled ? 0.6 : 1 }}
                isChecked={isChecked(option)}
                onChange={() => {
                  if (!readOnly || !disabled) onChangeHandler(option);
                }}
              >
                {option.label ? option.label : displayValue(option)}
              </Checkbox>
            ),
        )}
      </Flex>
    </CheckboxGroup>
  </FormControl>
);
